/**
 * ZEN ERP — AI Chat Widget
 * Admin kullanıcıları için sabit köşe konumlu yapay zeka sohbet paneli.
 */
import React, { useState, useContext } from "react";
import { X, Sparkles, Send, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../api";
import { AuthCtx } from "../appContexts";

export default function AIChatWidget({ onDashboard = false }) {
  const { user } = useContext(AuthCtx);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Merhaba! Ben ZEN Program Asistanınızım — hem kullanım uzmanı hem AI Wingman'iniz.\n\n💡 Kullanım soruları:\n• 'Nasıl sipariş girerim?'\n• 'Bayi iskontosunu nereden değiştiririm?'\n• 'WhatsApp botunu nasıl bağlarım?'\n• 'Tevkifatlı fatura nasıl düzenlenir?'\n\n⚙️ Veri & Fiyat değişiklikleri:\n• 'Art 90 200×300 için 950 yaz'\n• 'Artbey fiyatlarını %10 artır'\n\nYazma işlemlerinde önce önizleme gösteririm, sen onaylarsan uygulanır." }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [balanceWarning, setBalanceWarning] = useState(false);
  if (user?.role !== "admin") return null;

  const applyPending = async (action, msgIndex) => {
    try {
      setSending(true);
      const { data } = await api.post("/ai/apply", { tool: action.tool, args: action.args });
      const text = data.applied ? `✓ Uygulandı: ${JSON.stringify(data)}` : `✗ Hata: ${data.error || "bilinmeyen"}`;
      setMsgs(m => {
        const next = [...m];
        if (next[msgIndex]?.pendingActions) {
          next[msgIndex] = { ...next[msgIndex], pendingActions: next[msgIndex].pendingActions.filter(a => a !== action) };
        }
        return [...next, { role: "assistant", text }];
      });
    } catch (e) {
      setMsgs(m => [...m, { role: "assistant", text: "Uygulama hatası: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setSending(false); }
  };

  const rejectPending = (action, msgIndex) => {
    setMsgs(m => {
      const next = [...m];
      if (next[msgIndex]?.pendingActions) {
        next[msgIndex] = { ...next[msgIndex], pendingActions: next[msgIndex].pendingActions.filter(a => a !== action) };
      }
      return [...next, { role: "assistant", text: "İşlem iptal edildi." }];
    });
  };

  const send = async () => {
    if (!input.trim()) return;
    const u = input.trim();
    setMsgs([...msgs, { role: "user", text: u }]);
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: u, sessionId, dryRun: true });
      setSessionId(data.sessionId);
      if (data.balanceWarning) setBalanceWarning(true);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, pendingActions: data.pendingActions || [] }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Hata: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} className={`fixed right-5 z-40 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-xl opacity-55 hover:opacity-100 transition-all ${onDashboard ? "bottom-20 bg-gradient-to-br from-[#7C5CE6]/50 to-[#3B4FC4]/50 backdrop-blur-sm border border-white/20" : "bottom-5 bg-[#E5322A]"}`} data-testid="ai-chat-toggle">
        {open ? <X className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
      </button>
      {open && (
        <div className={`fixed right-5 z-40 w-96 max-w-[95vw] h-[560px] zen-panel flex flex-col shadow-2xl zen-anim-up ${onDashboard ? "bottom-40" : "bottom-24"}`}>
          <div className="p-4 border-b border-slate-200 dark:border-[#22304A] flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#E5322A] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div className="flex-1">
              <div className="text-sm font-bold">ZEN Program Asistanı</div>
              <div className="text-[10px] text-slate-500">Kullanım Uzmanı · Onaylı Yazma · Admin</div>
            </div>
            {balanceWarning && (
              <div className="flex items-center gap-1 bg-red-500/10 border border-red-500/40 text-red-600 dark:text-red-400 rounded-full px-2 py-0.5 text-[10px] font-semibold animate-pulse">
                <AlertCircle className="w-3 h-3" /> Bakiye Düşük
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#E5322A] text-white rounded-br-none" : "bg-slate-100 dark:bg-[#131B2E] rounded-bl-none"}`}>{m.text}</div>
                {m.pendingActions && m.pendingActions.length > 0 && (
                  <div className="mt-2 space-y-2 w-full max-w-[95%]">
                    {m.pendingActions.map((act, ai) => (
                      <div key={ai} className="zen-panel p-2.5 bg-amber-50 dark:bg-amber-500/10 border-amber-500/30 text-[11px]">
                        <div className="font-semibold mb-1.5 text-amber-800 dark:text-amber-300">{act.message}</div>
                        <div className="flex gap-1.5">
                          <button data-testid="ai-apply" onClick={() => applyPending(act, i)} disabled={sending} className="flex-1 bg-emerald-500 text-white py-1 rounded font-semibold text-[10px]"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Uygula</button>
                          <button onClick={() => rejectPending(act, i)} className="flex-1 border border-slate-300 py-1 rounded font-semibold text-[10px]">İptal</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {sending && <div className="text-xs text-slate-500 italic">Düşünüyor...</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 dark:border-[#22304A] flex gap-2">
            <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Örn: Art 90 200x300 için 950 yaz" className="zen-input flex-1 !py-2 !text-xs" />
            <button type="submit" disabled={sending} className="zen-btn-primary !px-3"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
